import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useVenues } from '../contexts/VenueContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import DashboardLayout from '../components/DashboardLayout';
import VenueCard from '../components/VenueCard';
import AddVenueModal from '../components/AddVenueModal';

const { FiPlus, FiHome, FiDollarSign, FiCalendar, FiBarChart3 } = FiIcons;

const Dashboard = () => {
  const { user } = useAuth();
  const { subscription, canAddVenue } = useSubscription();
  const { venues } = useVenues();
  const [showAddVenue, setShowAddVenue] = useState(false);

  const stats = [
    {
      title: 'Total Venues',
      value: venues.length,
      icon: FiHome,
      color: 'bg-blue-500'
    },
    {
      title: 'Total Bookings',
      value: '23',
      icon: FiCalendar,
      color: 'bg-green-500'
    },
    {
      title: 'Monthly Revenue',
      value: '€4,250',
      icon: FiDollarSign,
      color: 'bg-purple-500'
    },
    {
      title: 'Occupancy Rate',
      value: '78%',
      icon: FiBarChart3,
      color: 'bg-orange-500'
    }
  ];

  const handleAddVenue = () => {
    if (canAddVenue(venues.length)) {
      setShowAddVenue(true);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-1">Welcome back, {user?.name}!</p>
          </div>
          <button
            onClick={handleAddVenue}
            disabled={!canAddVenue(venues.length)}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-semibold transition-colors inline-flex items-center"
          >
            <SafeIcon icon={FiPlus} className="mr-2 h-5 w-5" />
            Add Venue
          </button>
        </div>

        {/* Subscription Status */}
        {subscription && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {subscription.plan.name} Plan
                </h3>
                <p className="text-gray-600">
                  {subscription.status === 'trial' 
                    ? `Trial ends ${new Date(subscription.trialEndsAt).toLocaleDateString()}`
                    : 'Active subscription'
                  }
                </p>
              </div>
              <Link
                to="/subscription"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                Manage Plan
              </Link>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-sm p-6"
            >
              <div className="flex items-center">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <SafeIcon icon={stat.icon} className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Venues */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Your Venues</h2>
            {!canAddVenue(venues.length) && (
              <p className="text-sm text-gray-500">
                Upgrade your plan to add more venues
              </p>
            )}
          </div>
          
          {venues.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <SafeIcon icon={FiHome} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No venues yet</h3>
              <p className="text-gray-600 mb-6">Create your first venue to start managing bookings</p>
              <button
                onClick={handleAddVenue}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors inline-flex items-center"
              >
                <SafeIcon icon={FiPlus} className="mr-2 h-5 w-5" />
                Add Your First Venue
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {venues.map((venue) => (
                <VenueCard key={venue.id} venue={venue} />
              ))}
            </div>
          )}
        </div>
      </div>

      {showAddVenue && (
        <AddVenueModal onClose={() => setShowAddVenue(false)} />
      )}
    </DashboardLayout>
  );
};

export default Dashboard;